import {
  OreHeading,
  OreInput,
  OreSelect,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { productContractingTranslation } from '@/Contract/ui/contractTranslation'
import { Step2Form } from '@/Contract/ui/hooks'
import { documentType } from '@/Shared/domain'
import { handleErrors } from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

interface ContractNewProductsStep2FormProps {
  isEditable?: boolean
  isDocumentEditable?: boolean
}

export const ContractNewProductsStep2Form = ({
  isEditable = true,
  isDocumentEditable = false,
}: ContractNewProductsStep2FormProps): JSX.Element => {
  const { t } = useTranslation()
  const formContext = useFormContext<Step2Form>()
  const { register, formState, trigger } = formContext
  const { errors } = handleErrors(formState)

  useEffect(() => {
    //trigger() // show errors on load
  }, [])

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(productContractingTranslation.contractProductPage.step2.form.title)}
      </OreHeading>
      <>
        {isEditable ? (
          <OreText>
            {t(productContractingTranslation.contractProductPage.step2.form.description.editable)}
          </OreText>
        ) : (
          <OreText>
            <Trans t={t} components={
              [
                <a href={`mailto:${import.meta.env.VITE_CONTACT_EMAIL}?subject=${t(productContractingTranslation.contractProductPage.step2.form.description.noEditableMailSubject)}`} />
              ]
            }>
              {t(productContractingTranslation.contractProductPage.step2.form.description.noEditable, {
                CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL,
              })}
            </Trans>
          </OreText>
        )}
      </>
      <OreInput
        {...register('name')}
        {...errors('name')}
        id="name"
        label={t(
          productContractingTranslation.contractProductPage.step2.form.data.name.label,
        )}
        placeholder={
          isEditable
            ? t(
              productContractingTranslation.contractProductPage.step2.form.data.name.placeholder,
            )
            : undefined
        }
        required
        maxLength={20}
        disabled={!isEditable}
      />
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('surname1')}
          {...errors('surname1')}
          id="surname1"
          label={t(
            productContractingTranslation.contractProductPage.step2.form.data.surname1
              .label,
          )}
          placeholder={
            isEditable
              ? t(
                productContractingTranslation.contractProductPage.step2.form.data
                  .surname1.placeholder,
              )
              : undefined
          }
          required
          maxLength={41}
          disabled={!isEditable}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreSelect
          {...register('documentType', {
            onChange: () => {
              trigger('documentNumber')
            },
          })}
          {...errors('documentType')}
          id="documentType"
          label={t(
            productContractingTranslation.contractProductPage.step2.form.data.documentType
              .label,
          )}
          legend={t(
            productContractingTranslation.contractProductPage.step2.form.data.documentType
              .legend,
          )}
          required
          disabled={!isDocumentEditable}
          defaultValue=""
        >
          <option value="" disabled hidden>
            {t(
              productContractingTranslation.contractProductPage.step2.form.data
                .documentType.placeholder,
            )}
          </option>
          <option value={documentType.DniNie}>
            {t(formTranslation.common.identifier.documentType.dni)}
          </option>
          <option value={documentType.Passport}>
            {t(formTranslation.common.identifier.documentType.passport)}
          </option>
        </OreSelect>
        <OreInput
          {...register('documentNumber')}
          {...errors('documentNumber')}
          id="documentNumber"
          label={t(
            productContractingTranslation.contractProductPage.step2.form.data
              .documentNumber.label,
          )}
          placeholder={
            isEditable
              ? t(
                productContractingTranslation.contractProductPage.step2.form.data
                  .documentNumber.placeholder,
              )
              : undefined
          }
          required
          disabled={!isDocumentEditable}
          maxLength={14}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('email')}
          {...errors('email')}
          id="email"
          label={t(
            productContractingTranslation.contractProductPage.step2.form.data.email.label,
          )}
          type="email"
          placeholder={
            isEditable
              ? t(
                productContractingTranslation.contractProductPage.step2.form.data.email
                  .placeholder,
              )
              : undefined
          }
          labelTooltip={t(
            productContractingTranslation.contractProductPage.step2.form.data.email
              .tooltip,
          )}
          required
          maxLength={255}
          disabled={!isEditable}
        />
        <OreInput
          {...register('phone')}
          {...errors('phone')}
          id="phone"
          label={t(
            productContractingTranslation.contractProductPage.step2.form.data.phone.label,
          )}
          type="tel"
          placeholder={
            isEditable
              ? t(
                productContractingTranslation.contractProductPage.step2.form.data.phone
                  .placeholder,
              )
              : undefined
          }
          labelTooltip={t(
            productContractingTranslation.contractProductPage.step2.form.data.phone
              .tooltip,
          )}
          required
          maxLength={255}
          disabled={!isEditable}
        />
      </OreStack>
    </OreStack>
  )
}
